import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
// 0615k: useNavigate eliminado - click persona ahora abre modal viewer-only.
import { useI18n } from '../../../../../i18n';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { PersonaViewerModal } from '../../components/PersonaViewerModal';
import {
  fetchGobernanzaAnalytics,
  fetchNegocioAnalytics,
  type GobernanzaAnalytics,
  type GobernanzaPersonaRow,
  type GobernanzaRolDetalle,
  type NegocioAnalytics,
} from '../../../../shared/services/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  Tooltip,
  ComposedChart,
  Line,
  PieChart,
  Pie,
} from 'recharts';

// ─── Corporate Palette ────────────────────────────────────────────────────

const C = {
  a1: '#1F3864',
  a2: '#2F5496',
  ce: '#33BCE1',
  ao: '#0E243E',
  as: '#D6E4F0',
  gc: '#F2F2F2',
  gm: '#D9D9D9',
  go: '#595959',
  w: '#FFF',
  b: '#000',
  tit: '#1F3864',
  verde: '#375623',
  rojo: '#7B0000',
  ambar: '#856404',
  verdeBg: '#E2EFDA',
  rojoBg: '#FCE4D6',
  ambarBg: '#FFF3CD',
  teal: '#1A94BF',
};

const GOB_COLORS: Record<string, string> = {
  PROPIETARIO_PROCESO: '#33BCE1',
  DPO: '#1D4ED8',
  OFICIAL_CUMPLIMIENTO: '#16A34A',
  AUDITOR_INTERNO: '#7C3AED',
  MIEMBRO_CONSEJO: '#B45309',
  SECRETARIO_CONSEJO: '#D97706',
  RESPONSABLE_CALIDAD: '#C2410C',
  PROPIETARIO_RIESGO: '#DC2626',
  PERSONALIZADO: '#6B7280',
};

const BAR_COLORS = [C.a1, C.a2, C.ce, C.teal];
const PIE_COLORS = [C.a1, C.a2, C.ce, C.teal, '#4472C4', C.as];

// ─── Types ─────────────────────────────────────────────────────────────────

type FilterKey = 'nivel' | 'tipoRol' | 'departamento' | 'empresa' | 'centro' | 'pais';

interface Filters {
  nivel: Set<string>;
  tipoRol: Set<string>;
  departamento: Set<string>;
  empresa: Set<string>;
  centro: Set<string>;
  pais: Set<string>;
}

const emptyFilters = (): Filters => ({
  nivel: new Set(),
  tipoRol: new Set(),
  departamento: new Set(),
  empresa: new Set(),
  centro: new Set(),
  pais: new Set(),
});

interface PeopleAnalyticsDashboardProps {
  embedded?: boolean;
}

interface GobPersonaExt extends GobernanzaPersonaRow {
  puesto?: string;
  nivel?: string;
  departamento?: string;
}

interface MergedPersona {
  id: string;
  nombre: string;
  apellidos: string;
  puesto: string | null;
  nivel: string | null;
  departamento: string | null;
  empresa: string | null;
  centro: string | null;
  pais: string | null;
  direccion: string | null;
  superior: string | null;
  superiorId: string | null;
  roles: GobernanzaRolDetalle[];
  numRoles: number;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const pct = (a: number, b: number): number => (b ? Math.round((a / b) * 100) : 0);

function abreviar(text: string, max: number = 16): string {
  if (!text) return '';
  const t = text
    .replace(/Direcci[oó]n de /gi, 'Dir. ')
    .replace(/Direcci[oó]n /gi, 'Dir. ')
    .replace(/Administraci[oó]n/gi, 'Admin.')
    .replace(/Operaciones/gi, 'Oper.')
    .replace(/Tecnol[oó]gica/gi, 'Tecn.')
    .replace(/Corporativa/gi, 'Corp.')
    .replace(/Ingenier[ií]a/gi, 'Ing.')
    .replace(/Reputacional/gi, 'Reput.')
    .replace(/Cumplimiento/gi, 'Cumpl.')
    .replace(/Gobernanza/gi, 'Gob.')
    .replace(/Recursos Humanos/gi, 'RRHH')
    .replace(/Responsable/gi, 'Resp.')
    .replace(/Desarrollador/gi, 'Desarr.')
    .replace(/Ingeniero/gi, 'Ing.')
    .replace(/PROPIETARIO_PROCESO/g, 'Prop. Proceso')
    .replace(/OFICIAL_CUMPLIMIENTO/g, 'Of. Cumplim.')
    .replace(/AUDITOR_INTERNO/g, 'Auditor Int.')
    .replace(/MIEMBRO_CONSEJO/g, 'Miembro Cons.')
    .replace(/RESPONSABLE_CALIDAD/g, 'Resp. Calidad')
    .replace(/SECRETARIO_CONSEJO/g, 'Secret. Cons.')
    .replace(/PROPIETARIO_RIESGO/g, 'Prop. Riesgo')
    .replace(/PERSONALIZADO/g, 'Personalizado')
    .replace(/_/g, ' ');
  return t.length <= max ? t : t.slice(0, max - 1) + '…';
}

// ─── Compact styles ───────────────────────────────────────────────────────

const cardStyle: React.CSSProperties = {
  background: C.w,
  border: `0.5px solid ${C.gm}`,
  borderRadius: 6,
  padding: '8px 10px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minHeight: 0,
};

const SecTitle = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 800,
      color: C.tit,
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      paddingBottom: 2,
      borderBottom: `1.5px solid ${C.tit}`,
      marginBottom: 4,
    }}
  >
    {children}
  </div>
);

interface BLabelProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  value?: number | string;
}

const BLabel = ({ x = 0, y = 0, width = 0, height = 0, value }: BLabelProps) => (
  <text x={x + width + 3} y={y + height / 2 + 4} fontSize={11} fill={C.b} fontWeight="800">
    {value}
  </text>
);

const EmptyChart = () => (
  <div
    style={{
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: C.go,
      fontStyle: 'italic',
      fontSize: 11,
    }}
  >
    Sin datos suficientes
  </div>
);

const SEV: Record<string, { bg: string; border: string; text: string }> = {
  critico: { bg: C.rojoBg, border: C.rojo, text: C.rojo },
  alto: { bg: C.rojoBg, border: C.rojo, text: C.rojo },
  medio: { bg: C.ambarBg, border: C.ambar, text: C.ambar },
  bajo: { bg: '#DBEAFE', border: '#1D4ED8', text: '#1D4ED8' },
};

// ─── CSS Injection ─────────────────────────────────────────────────────────

const STYLE_ID = 'people-analytics-style';

function useInjectStyles() {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .pa-root *:focus, .pa-root *:focus-visible, .pa-root *:active {
        outline: none !important; box-shadow: none !important;
        -webkit-tap-highlight-color: transparent !important;
      }
      .pa-root .recharts-wrapper, .pa-root .recharts-surface { outline: none !important; }
      .pa-root .pa-table td { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 0; }
    `;
    document.head.appendChild(style);
    return () => {
      document.getElementById(STYLE_ID)?.remove();
    };
  }, []);
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export function PeopleAnalyticsDashboard({ embedded }: PeopleAnalyticsDashboardProps) {
  const { t } = useI18n();
  const u = ((t.structure as Record<string, unknown>)?.analytics as Record<string, unknown>)
    ?.unified as Record<string, string> | undefined;
  const uk = u ?? {};

  const [gob, setGob] = useState<GobernanzaAnalytics | null>(null);
  const [neg, setNeg] = useState<NegocioAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flt, setFlt] = useState<Filters>(emptyFilters);
  // 0615k: viewer-only modal de persona (click en fila persona del directorio).
  const [viewerPersonaId, setViewerPersonaId] = useState<string | null>(null);

  const ctrlRef = useRef(false);

  useInjectStyles();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) ctrlRef.current = true;
    };
    const up = () => {
      ctrlRef.current = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  useEffect(() => {
    Promise.all([fetchGobernanzaAnalytics(), fetchNegocioAnalytics()])
      .then(([gobRes, negRes]) => {
        setGob(gobRes.data);
        setNeg(negRes.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // ─── Cross-filter logic (TRANSVERSAL) ────────────────────────────────────
  const has =
    flt.nivel.size > 0 ||
    flt.tipoRol.size > 0 ||
    flt.departamento.size > 0 ||
    flt.empresa.size > 0 ||
    flt.centro.size > 0 ||
    flt.pais.size > 0;

  const tog = useCallback((k: FilterKey, v: string) => {
    setFlt((prev) => {
      const ns = new Set(prev[k]);
      if (ctrlRef.current) {
        if (ns.has(v)) ns.delete(v);
        else ns.add(v);
      } else {
        if (ns.size === 1 && ns.has(v)) ns.clear();
        else {
          ns.clear();
          ns.add(v);
        }
      }
      return { ...prev, [k]: ns };
    });
  }, []);

  const clr = useCallback(() => setFlt(emptyFilters()), []);

  const dim = (k: FilterKey, v: string): React.CSSProperties =>
    has && flt[k].size > 0 && !flt[k].has(v)
      ? { opacity: 0.18, transition: 'opacity .2s' }
      : { opacity: 1, transition: 'opacity .2s' };

  // ─── Derived data ────────────────────────────────────────────────────────
  const gobPersonas = (gob?.personas ?? []) as GobPersonaExt[];
  const concentracion = gob?.concentracionRoles ?? [];
  const rolesPorTipo = gob?.rolesPorTipo ?? [];
  const coberturaNivel = (gob?.coberturaPorNivel ?? []).filter((r) => r.total > 0);
  const alertas = gob?.alertas ?? [];
  const vacantesDetalle = gob?.vacantesDetalle ?? [];
  const gKpis = gob?.kpis;

  const negPersonas = neg?.personas ?? [];
  const evolucion = neg?.evolucionMensual ?? [];
  const porDireccion = (neg?.porDireccion ?? []).slice(0, 8);
  const porPuesto = (neg?.porPuesto ?? []).slice(0, 8);
  const porEmpresa = (neg?.porEmpresa ?? []).slice(0, 6);
  const porCentro = (neg?.porCentro ?? []).slice(0, 6);
  const porPais = neg?.porPais ?? [];
  const nKpis = neg?.kpis;

  // ─── Merged personas ────────────────────────────────────────────────────
  const mergedPersonas: MergedPersona[] = useMemo(() => {
    const gobMap = new Map(gobPersonas.map((p) => [p.id, p]));
    const negMap = new Map(negPersonas.map((p) => [p.id, p]));
    const allIds = new Set([...gobMap.keys(), ...negMap.keys()]);
    return Array.from(allIds).map((id) => {
      const g = gobMap.get(id);
      const n = negMap.get(id);
      return {
        id,
        nombre: g?.nombre ?? n?.nombre ?? '',
        apellidos: g?.apellidos ?? n?.apellidos ?? '',
        puesto: (g as GobPersonaExt)?.puesto ?? n?.puesto ?? null,
        nivel: (g as GobPersonaExt)?.nivel ?? null,
        departamento: (g as GobPersonaExt)?.departamento ?? null,
        empresa: n?.empresa ?? null,
        centro: n?.centro ?? null,
        pais: n?.pais ?? null,
        direccion: n?.direccion ?? null,
        superior: n?.superior ?? null,
        superiorId: n?.superiorId ?? null,
        roles: g?.roles ?? [],
        numRoles: g?.numRoles ?? 0,
      };
    });
  }, [gobPersonas, negPersonas]);

  // ─── Filtered personas (TRANSVERSAL) ─────────────────────────────────────
  const filteredPersonas = useMemo(() => {
    if (!has) return mergedPersonas;
    return mergedPersonas.filter((p) => {
      if (flt.nivel.size > 0 && (!p.nivel || !flt.nivel.has(p.nivel))) return false;
      if (flt.departamento.size > 0 && (!p.departamento || !flt.departamento.has(p.departamento)))
        return false;
      if (flt.empresa.size > 0 && (!p.empresa || !flt.empresa.has(p.empresa))) return false;
      if (flt.centro.size > 0 && (!p.centro || !flt.centro.has(p.centro))) return false;
      if (flt.pais.size > 0 && (!p.pais || !flt.pais.has(p.pais))) return false;
      if (flt.tipoRol.size > 0) {
        if (!p.roles.some((r) => flt.tipoRol.has(r.tipoRol))) return false;
      }
      return true;
    });
  }, [mergedPersonas, flt, has]);

  // ─── KPI computed ────────────────────────────────────────────────────────
  const coverageRate = gKpis ? pct(gKpis.puestosConRol, gKpis.totalPuestos) : 0;
  const occupancyRate = gKpis ? pct(gKpis.totalPersonas, gKpis.totalPuestos) : 0;
  const fichasProgress = gKpis ? pct(gKpis.fichasPuesto, gKpis.totalPuestos) : 0;

  // ─── Active filter pills ─────────────────────────────────────────────────
  const activePills = useMemo(() => {
    const pills: Array<{ key: FilterKey; value: string; label: string }> = [];
    for (const v of flt.nivel) pills.push({ key: 'nivel', value: v, label: `Nivel: ${v}` });
    for (const v of flt.tipoRol)
      pills.push({ key: 'tipoRol', value: v, label: `Rol: ${v.replace(/_/g, ' ')}` });
    for (const v of flt.departamento)
      pills.push({ key: 'departamento', value: v, label: `Depto: ${v}` });
    for (const v of flt.empresa) pills.push({ key: 'empresa', value: v, label: `Empresa: ${v}` });
    for (const v of flt.centro) pills.push({ key: 'centro', value: v, label: `Centro: ${v}` });
    for (const v of flt.pais) pills.push({ key: 'pais', value: v, label: `País: ${v}` });
    return pills;
  }, [flt]);

  const removePill = useCallback((k: FilterKey, v: string) => {
    setFlt((prev) => {
      const ns = new Set(prev[k]);
      ns.delete(v);
      return { ...prev, [k]: ns };
    });
  }, []);

  // ─── Loading / Error / Empty ─────────────────────────────────────────────

  const containerH = embedded ? '100%' : 'calc(100vh - 88px)';
  const baseBg: React.CSSProperties = {
    background: 'var(--wl-background, #ECEEF2)',
    fontFamily: "'DM Sans',sans-serif",
  };

  if (loading) {
    return (
      <div
        style={{
          height: containerH,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...baseBg,
        }}
      >
        <LoadingSpinner text={uk.loading ?? 'Cargando People Analytics...'} size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: containerH,
          ...baseBg,
        }}
      >
        <div style={{ textAlign: 'center', color: C.rojo }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
            Error al cargar datos
          </div>
          <div style={{ fontSize: 13 }}>{error}</div>
        </div>
      </div>
    );
  }

  if (!gob && !neg) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: containerH,
          ...baseBg,
          color: C.go,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Sin datos</div>
          <div style={{ fontSize: 13 }}>No hay datos de People Analytics disponibles.</div>
        </div>
      </div>
    );
  }

  // ─── KPI definitions (6 gob + 6 neg = 12) ───────────────────────────────

  const semaforo = (v: number) => (v >= 80 ? C.verde : v >= 40 ? C.ambar : C.rojo);

  const kpiItems = [
    {
      key: 'cob',
      value: `${coverageRate}%`,
      label: uk.coverageRoles ?? 'Cobertura',
      color: semaforo(coverageRate),
      sub: `${gKpis?.puestosConRol ?? 0}/${gKpis?.totalPuestos ?? 0}`,
    },
    {
      key: 'vac',
      value: gKpis?.vacantes ?? 0,
      label: uk.vacancies ?? 'Vacantes',
      color: (gKpis?.vacantes ?? 0) > 0 ? C.rojo : C.verde,
    },
    {
      key: 'bus',
      value: gKpis?.busFactor ?? 0,
      label: uk.busFactor ?? 'Bus factor',
      color: (gKpis?.busFactor ?? 0) > 2 ? C.ambar : C.a2,
    },
    { key: 'rol', value: gKpis?.rolesActivos ?? 0, label: uk.activeRoles ?? 'Roles', color: C.ce },
    {
      key: 'fic',
      value: `${fichasProgress}%`,
      label: uk.jobDescriptions ?? 'Fichas',
      color: semaforo(fichasProgress),
      sub: `${gKpis?.fichasPuesto ?? 0}/${gKpis?.totalPuestos ?? 0}`,
    },
    {
      key: 'ocu',
      value: `${occupancyRate}%`,
      label: uk.occupancy ?? 'Ocupación',
      color: semaforo(occupancyRate),
    },
    {
      key: 'per',
      value: nKpis?.totalPersonas ?? 0,
      label: uk.activePeople ?? 'Personas',
      color: C.a1,
      sub: `+${nKpis?.altasUltimos3Meses ?? 0}`,
    },
    {
      key: 'emp',
      value: nKpis?.totalEmpresas ?? 0,
      label: uk.companies ?? 'Empresas',
      color: C.a2,
    },
    { key: 'pue', value: nKpis?.totalPuestos ?? 0, label: uk.positions ?? 'Puestos', color: C.ce },
    {
      key: 'dir',
      value: nKpis?.totalDirecciones ?? 0,
      label: uk.departments ?? 'Direcciones',
      color: C.teal,
    },
    { key: 'pai', value: nKpis?.totalPaises ?? 0, label: uk.countries ?? 'Países', color: C.a2 },
    {
      key: 'alt',
      value: nKpis?.altasUltimos3Meses ?? 0,
      label: uk.recentHires ?? 'Altas 3m',
      color: (nKpis?.altasUltimos3Meses ?? 0) > 0 ? C.verde : C.go,
    },
  ];

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div
      className="pa-root"
      style={{
        display: 'grid',
        gridTemplateRows: '48px 250px 200px auto 1fr',
        gap: 5,
        height: containerH,
        padding: '6px 20px',
        overflow: 'hidden',
        ...baseBg,
      }}
    >
      {/* ══ ROW 1: 12 KPI Cards ══ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 4 }}>
        {kpiItems.map((item) => (
          <div
            key={item.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px',
              height: 40,
              background: '#fff',
              borderRadius: 5,
              border: `0.5px solid ${C.gm}`,
              borderLeftWidth: 3,
              borderLeftColor: item.color,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  fontFamily: "'JetBrains Mono',monospace",
                  color: item.color,
                  lineHeight: 1,
                }}
              >
                {item.value}
              </div>
              <div
                style={{
                  fontSize: 7.5,
                  fontWeight: 600,
                  color: C.go,
                  textTransform: 'uppercase',
                  letterSpacing: 0.3,
                }}
              >
                {item.label}
              </div>
              {'sub' in item && item.sub && (
                <div style={{ fontSize: 7, color: C.go }}>{item.sub}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ══ ROW 2: 6 charts — Bus Factor | Roles | Vacantes ║ Evolucion | Direccion | Puesto ══ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.2fr .8fr .7fr 1.3fr .8fr .8fr',
          gap: 5,
          minHeight: 0,
        }}
      >
        {/* Bus Factor */}
        <div style={{ ...cardStyle, borderTop: `2px solid ${C.tit}` }}>
          <SecTitle>{uk.busFactorChart ?? 'Bus factor'}</SecTitle>
          {concentracion.length === 0 ? (
            <EmptyChart />
          ) : (
            <div style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={concentracion}
                  layout="vertical"
                  margin={{ left: 105, right: 25, top: 2, bottom: 2 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="nombre"
                    width={100}
                    tick={{ fontSize: 9, fill: C.go }}
                    tickFormatter={(v: string) => abreviar(v, 16)}
                  />
                  <Tooltip
                    formatter={(v: unknown) => [Number(v), 'Roles']}
                    contentStyle={{ fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}
                  />
                  <Bar
                    dataKey="numRoles"
                    label={<BLabel />}
                    radius={[0, 3, 3, 0]}
                    barSize={14}
                    onClick={(_: unknown, idx: number) => {
                      const item = concentracion[idx];
                      if (item) tog('departamento', item.nombre);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {concentracion.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={entry.numRoles >= 3 ? C.rojo : entry.numRoles === 2 ? C.ambar : C.a1}
                        style={dim('departamento', entry.nombre)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Roles por tipo */}
        <div style={{ ...cardStyle, borderTop: `2px solid ${C.tit}` }}>
          <SecTitle>{uk.rolesByType ?? 'Roles por tipo'}</SecTitle>
          {rolesPorTipo.length === 0 ? (
            <EmptyChart />
          ) : (
            <div style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={rolesPorTipo}
                  layout="vertical"
                  margin={{ left: 105, right: 20, top: 2, bottom: 2 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="tipo"
                    width={100}
                    tick={{ fontSize: 9, fill: C.go }}
                    tickFormatter={(v: string) => abreviar(v, 16)}
                  />
                  <Tooltip
                    formatter={(v: unknown) => [Number(v), 'Asig.']}
                    contentStyle={{ fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}
                  />
                  <Bar
                    dataKey="count"
                    label={<BLabel />}
                    radius={[0, 3, 3, 0]}
                    barSize={14}
                    onClick={(_: unknown, idx: number) => {
                      const item = rolesPorTipo[idx];
                      if (item) tog('tipoRol', item.tipo);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {rolesPorTipo.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={GOB_COLORS[entry.tipo] ?? C.go}
                        style={dim('tipoRol', entry.tipo)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Vacantes detalle */}
        <div style={{ ...cardStyle, borderTop: `2px solid ${C.tit}` }}>
          <SecTitle>{uk.vacancies ?? 'Vacantes'}</SecTitle>
          {vacantesDetalle.length === 0 ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: C.verde,
                fontStyle: 'italic',
                fontSize: 11,
              }}
            >
              Sin vacantes
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
              }}
            >
              {vacantesDetalle.map((v, i) => (
                <div
                  key={i}
                  style={{
                    padding: '3px 6px',
                    borderBottom: `1px solid ${C.gc}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: C.a1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {v.titulo}
                    </span>
                    {v.esCritica && (
                      <span
                        style={{
                          fontSize: 7,
                          fontWeight: 800,
                          color: C.rojo,
                          background: C.rojoBg,
                          padding: '0 4px',
                          borderRadius: 3,
                          textTransform: 'uppercase',
                        }}
                      >
                        critica
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <span
                      style={{
                        fontSize: 7,
                        fontWeight: 800,
                        background: C.as,
                        color: C.a1,
                        padding: '0 4px',
                        borderRadius: 6,
                      }}
                    >
                      {v.nivel}
                    </span>
                    <span style={{ fontSize: 8, color: C.go }}>
                      {v.departamento?.nombre ?? '-'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Evolucion temporal */}
        <div style={{ ...cardStyle, borderTop: `2px solid ${C.teal}` }}>
          <SecTitle>{uk.evolution ?? 'Evolucion'}</SecTitle>
          {evolucion.length < 3 ? (
            <EmptyChart />
          ) : (
            <>
              <div style={{ flex: 1, minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={evolucion}
                    margin={{ top: 4, right: 15, left: 5, bottom: 16 }}
                  >
                    <XAxis dataKey="mesLabel" tick={{ fontSize: 9, fill: C.go }} />
                    <YAxis hide />
                    <Tooltip contentStyle={{ fontSize: 11, fontFamily: "'DM Sans',sans-serif" }} />
                    <Bar
                      dataKey="altas"
                      fill={C.a2}
                      barSize={14}
                      radius={[3, 3, 0, 0]}
                      name="Altas"
                    />
                    <Line
                      dataKey="acumulado"
                      stroke={C.ce}
                      strokeWidth={1.5}
                      dot={false}
                      name="Acumulado"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    fontSize: 9,
                    color: C.go,
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: 1,
                      background: C.a2,
                      display: 'inline-block',
                    }}
                  />{' '}
                  Altas
                </span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    fontSize: 9,
                    color: C.go,
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: C.ce,
                      display: 'inline-block',
                    }}
                  />{' '}
                  Acum.
                </span>
              </div>
            </>
          )}
        </div>

        {/* Por direccion */}
        <div style={{ ...cardStyle, borderTop: `2px solid ${C.teal}` }}>
          <SecTitle>{uk.byDepartment ?? 'Por direccion'}</SecTitle>
          {porDireccion.length === 0 ? (
            <EmptyChart />
          ) : (
            <div style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={porDireccion}
                  layout="vertical"
                  margin={{ left: 105, right: 20, top: 2, bottom: 2 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={100}
                    tick={{ fontSize: 9, fill: C.go }}
                    tickFormatter={(v: string) => abreviar(v, 16)}
                  />
                  <Tooltip contentStyle={{ fontSize: 11, fontFamily: "'DM Sans',sans-serif" }} />
                  <Bar
                    dataKey="value"
                    label={<BLabel />}
                    radius={[0, 3, 3, 0]}
                    barSize={14}
                    onClick={(_: unknown, idx: number) => {
                      const item = porDireccion[idx];
                      if (item) tog('departamento', item.name);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {porDireccion.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={BAR_COLORS[i % BAR_COLORS.length]}
                        style={dim('departamento', entry.name)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Por puesto */}
        <div style={{ ...cardStyle, borderTop: `2px solid ${C.teal}` }}>
          <SecTitle>{uk.byPosition ?? 'Por puesto'}</SecTitle>
          {porPuesto.length === 0 ? (
            <EmptyChart />
          ) : (
            <div style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={porPuesto}
                  layout="vertical"
                  margin={{ left: 105, right: 20, top: 2, bottom: 2 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={100}
                    tick={{ fontSize: 9, fill: C.go }}
                    tickFormatter={(v: string) => abreviar(v, 16)}
                  />
                  <Tooltip contentStyle={{ fontSize: 11, fontFamily: "'DM Sans',sans-serif" }} />
                  <Bar dataKey="value" label={<BLabel />} radius={[0, 3, 3, 0]} barSize={14}>
                    {porPuesto.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={BAR_COLORS[i % BAR_COLORS.length]}
                        style={dim('departamento', entry.name)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* ══ ROW 3: 6 secondary panels (3 gob + 3 neg) ══ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 5, minHeight: 0 }}>
        {/* Cobertura por nivel */}
        <div style={{ ...cardStyle }}>
          <SecTitle>{uk.coverageByLevel ?? 'Cobertura nivel'}</SecTitle>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: C.ao, color: C.w }}>
                  <th
                    style={{
                      padding: '3px 5px',
                      textAlign: 'left',
                      fontWeight: 700,
                      fontSize: 8,
                      textTransform: 'uppercase',
                    }}
                  >
                    Nivel
                  </th>
                  <th
                    style={{
                      padding: '3px 5px',
                      textAlign: 'center',
                      fontWeight: 700,
                      fontSize: 8,
                      textTransform: 'uppercase',
                    }}
                  >
                    P
                  </th>
                  <th
                    style={{
                      padding: '3px 5px',
                      textAlign: 'center',
                      fontWeight: 700,
                      fontSize: 8,
                      textTransform: 'uppercase',
                    }}
                  >
                    R
                  </th>
                  <th
                    style={{
                      padding: '3px 5px',
                      textAlign: 'center',
                      fontWeight: 700,
                      fontSize: 8,
                      textTransform: 'uppercase',
                    }}
                  >
                    %
                  </th>
                </tr>
              </thead>
              <tbody>
                {coberturaNivel.map((row) => {
                  const pctColor =
                    row.porcentaje >= 80 ? C.verde : row.porcentaje >= 40 ? C.ambar : C.rojo;
                  return (
                    <tr
                      key={row.nivel}
                      onClick={() => tog('nivel', row.nivel)}
                      style={{
                        cursor: 'pointer',
                        borderBottom: `1px solid ${C.gc}`,
                        ...dim('nivel', row.nivel),
                      }}
                    >
                      <td style={{ padding: '2px 5px' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '0px 5px',
                            borderRadius: 8,
                            background: C.as,
                            color: C.a1,
                            fontSize: 8,
                            fontWeight: 800,
                          }}
                        >
                          {row.nivel}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '2px 5px',
                          textAlign: 'center',
                          fontWeight: 700,
                          fontSize: 10,
                        }}
                      >
                        {row.total}
                      </td>
                      <td
                        style={{
                          padding: '2px 5px',
                          textAlign: 'center',
                          fontWeight: 700,
                          fontSize: 10,
                        }}
                      >
                        {row.conRol}
                      </td>
                      <td style={{ padding: '2px 5px', textAlign: 'center', fontSize: 10 }}>
                        <span style={{ fontWeight: 800, color: pctColor }}>{row.porcentaje}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Alertas */}
        <div style={{ ...cardStyle }}>
          <SecTitle>{uk.alerts ?? 'Alertas'}</SecTitle>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {alertas.length === 0 && (
              <div style={{ fontSize: 11, color: C.go, fontStyle: 'italic', padding: 4 }}>
                Sin alertas
              </div>
            )}
            {alertas.slice(0, 5).map((a, i) => {
              const s = SEV[a.severidad] ?? SEV.bajo;
              return (
                <div
                  key={i}
                  style={{
                    background: s.bg,
                    borderLeft: `2px solid ${s.border}`,
                    borderRadius: 3,
                    padding: '3px 6px',
                    fontSize: 9,
                    color: s.text,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 7,
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      opacity: 0.7,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {a.severidad}
                  </span>
                  <span
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {a.mensaje}
                  </span>
                </div>
              );
            })}
            {alertas.length > 5 && (
              <div
                style={{
                  fontSize: 9,
                  color: C.go,
                  fontStyle: 'italic',
                  padding: '2px 6px',
                  textAlign: 'center',
                }}
              >
                +{alertas.length - 5} más
              </div>
            )}
          </div>
        </div>

        {/* Vacantes por departamento */}
        <div style={{ ...cardStyle }}>
          <SecTitle>{uk.vacancyDistribution ?? 'Vacantes depto'}</SecTitle>
          {vacantesDetalle.length === 0 ? (
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: C.verde,
                fontStyle: 'italic',
                fontSize: 11,
              }}
            >
              Sin vacantes
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {(() => {
                const map = new Map<string, number>();
                vacantesDetalle.forEach((v) => {
                  const d = v.departamento?.nombre ?? 'Sin depto';
                  map.set(d, (map.get(d) ?? 0) + 1);
                });
                return Array.from(map.entries())
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 6)
                  .map(([dept, count], i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '2px 6px',
                        borderBottom: `1px solid ${C.gc}`,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 10,
                          color: C.go,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '70%',
                        }}
                      >
                        {dept}
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 800,
                          fontFamily: "'JetBrains Mono',monospace",
                          color: C.rojo,
                        }}
                      >
                        {count}
                      </span>
                    </div>
                  ));
              })()}
            </div>
          )}
        </div>

        {/* Por empresa */}
        <div style={{ ...cardStyle, borderTop: `2px solid ${C.teal}` }}>
          <SecTitle>{uk.byCompany ?? 'Por empresa'}</SecTitle>
          {porEmpresa.length === 0 ? (
            <EmptyChart />
          ) : (
            <div style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={porEmpresa}
                  layout="vertical"
                  margin={{ left: 105, right: 20, top: 2, bottom: 2 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={100}
                    tick={{ fontSize: 9, fill: C.go }}
                    tickFormatter={(v: string) => abreviar(v, 16)}
                  />
                  <Tooltip contentStyle={{ fontSize: 11, fontFamily: "'DM Sans',sans-serif" }} />
                  <Bar
                    dataKey="value"
                    label={<BLabel />}
                    radius={[0, 3, 3, 0]}
                    barSize={14}
                    onClick={(_: unknown, idx: number) => {
                      const item = porEmpresa[idx];
                      if (item) tog('empresa', item.name);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {porEmpresa.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={BAR_COLORS[i % BAR_COLORS.length]}
                        style={dim('empresa', entry.name)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Por centro */}
        <div style={{ ...cardStyle, borderTop: `2px solid ${C.teal}` }}>
          <SecTitle>{uk.bySite ?? 'Por centro'}</SecTitle>
          {porCentro.length === 0 ? (
            <EmptyChart />
          ) : (
            <div style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={porCentro}
                  layout="vertical"
                  margin={{ left: 105, right: 20, top: 2, bottom: 2 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={100}
                    tick={{ fontSize: 9, fill: C.go }}
                    tickFormatter={(v: string) => abreviar(v, 16)}
                  />
                  <Tooltip contentStyle={{ fontSize: 11, fontFamily: "'DM Sans',sans-serif" }} />
                  <Bar
                    dataKey="value"
                    label={<BLabel />}
                    radius={[0, 3, 3, 0]}
                    barSize={14}
                    onClick={(_: unknown, idx: number) => {
                      const item = porCentro[idx];
                      if (item) tog('centro', item.name);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {porCentro.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={BAR_COLORS[i % BAR_COLORS.length]}
                        style={dim('centro', entry.name)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Por pais (donut) */}
        <div style={{ ...cardStyle, borderTop: `2px solid ${C.teal}` }}>
          <SecTitle>{uk.byCountry ?? 'Por pais'}</SecTitle>
          {porPais.length === 0 ? (
            <EmptyChart />
          ) : (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ flex: '0 0 90px', height: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={porPais}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={24}
                      outerRadius={44}
                      paddingAngle={2}
                      onClick={(_: unknown, idx: number) => {
                        const item = porPais[idx];
                        if (item) tog('pais', item.name);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {porPais.map((entry, i) => (
                        <Cell
                          key={i}
                          fill={PIE_COLORS[i % PIE_COLORS.length]}
                          style={dim('pais', entry.name)}
                        />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 10, fontFamily: "'DM Sans',sans-serif" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {porPais.map((entry, i) => (
                  <span
                    key={i}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3,
                      fontSize: 9,
                      color: C.go,
                    }}
                  >
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: PIE_COLORS[i % PIE_COLORS.length],
                        display: 'inline-block',
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontWeight: 700,
                        fontFamily: "'JetBrains Mono',monospace",
                        color: C.a1,
                      }}
                    >
                      {entry.value}
                    </span>
                    {entry.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ══ ROW 4: Filter Pills ══ */}
      {has ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            alignItems: 'center',
          }}
        >
          {activePills.map((pill, i) => (
            <span
              key={i}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                background: C.as,
                color: C.a1,
                fontSize: 9,
                fontWeight: 700,
                padding: '1px 7px',
                borderRadius: 8,
                whiteSpace: 'nowrap',
              }}
            >
              {pill.label}
              <button
                onClick={() => removePill(pill.key, pill.value)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: C.a1,
                  cursor: 'pointer',
                  fontSize: 11,
                  fontWeight: 900,
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                &times;
              </button>
            </span>
          ))}
          <button
            onClick={clr}
            style={{
              background: C.ce,
              color: C.ao,
              border: 'none',
              borderRadius: 8,
              padding: '1px 8px',
              fontSize: 9,
              fontWeight: 800,
              cursor: 'pointer',
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            {uk.clearFilters ?? 'Limpiar filtros'}
          </button>
        </div>
      ) : (
        <div />
      )}

      {/* ══ ROW 5: Unified detail table (11 columns) ══ */}
      <div style={{ ...cardStyle, padding: '6px 8px', minHeight: 0, overflow: 'hidden' }}>
        <SecTitle>{uk.detailTable ?? 'Directorio de personas'}</SecTitle>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <table
            className="pa-table"
            style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
          >
            <thead>
              <tr style={{ background: C.ao, color: C.w, position: 'sticky', top: 0, zIndex: 1 }}>
                <th
                  style={{
                    padding: '3px 5px',
                    textAlign: 'left',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 140,
                  }}
                >
                  Nombre
                </th>
                <th
                  style={{
                    padding: '3px 5px',
                    textAlign: 'left',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 150,
                  }}
                >
                  Puesto
                </th>
                <th
                  style={{
                    padding: '3px 5px',
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 70,
                  }}
                >
                  Nivel
                </th>
                <th
                  style={{
                    padding: '3px 5px',
                    textAlign: 'left',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 120,
                  }}
                >
                  Departamento
                </th>
                <th
                  style={{
                    padding: '3px 5px',
                    textAlign: 'left',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 120,
                  }}
                >
                  {uk.superior ?? 'Superior'}
                </th>
                <th
                  style={{
                    padding: '3px 5px',
                    textAlign: 'left',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 130,
                  }}
                >
                  Empresa
                </th>
                <th
                  style={{
                    padding: '3px 5px',
                    textAlign: 'left',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 110,
                  }}
                >
                  Centro
                </th>
                <th
                  style={{
                    padding: '3px 5px',
                    textAlign: 'left',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 60,
                  }}
                >
                  Pais
                </th>
                <th
                  style={{
                    padding: '3px 5px',
                    textAlign: 'left',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                  }}
                >
                  Roles
                </th>
                <th
                  style={{
                    padding: '3px 5px',
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: 7.5,
                    textTransform: 'uppercase',
                    width: 30,
                  }}
                >
                  N.
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredPersonas.map((p) => (
                <tr
                  key={p.id}
                  // 0615k: abrir modal viewer-only en lugar de navegar a admin.
                  onClick={() => setViewerPersonaId(p.id)}
                  style={{ cursor: 'pointer', borderBottom: `1px solid ${C.gc}` }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = C.gc;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                  }}
                >
                  <td style={{ padding: '2px 5px', fontWeight: 600, fontSize: 10 }}>
                    {p.nombre} {p.apellidos}
                  </td>
                  <td style={{ padding: '2px 5px', color: C.go, fontSize: 10 }}>
                    {p.puesto ?? '-'}
                  </td>
                  <td style={{ padding: '2px 5px', textAlign: 'center', fontSize: 10 }}>
                    {p.nivel ? (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0px 5px',
                          borderRadius: 8,
                          background: C.as,
                          color: C.a1,
                          fontSize: 8,
                          fontWeight: 800,
                        }}
                      >
                        {p.nivel}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td style={{ padding: '2px 5px', color: C.go, fontSize: 10 }}>
                    {p.departamento ?? '-'}
                  </td>
                  <td style={{ padding: '2px 5px', fontSize: 10 }}>
                    {p.superior ? (
                      <span
                        style={{ color: C.a2, cursor: 'pointer', fontWeight: 500 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          // 0615k: viewer-only del superior (mismo modal).
                          if (p.superiorId) setViewerPersonaId(p.superiorId);
                        }}
                      >
                        {p.superior}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td style={{ padding: '2px 5px', color: C.go, fontSize: 10 }}>
                    {p.empresa ?? '-'}
                  </td>
                  <td style={{ padding: '2px 5px', color: C.go, fontSize: 10 }}>
                    {p.centro ?? '-'}
                  </td>
                  <td style={{ padding: '2px 5px', color: C.go, fontSize: 10 }}>{p.pais ?? '-'}</td>
                  <td style={{ padding: '2px 5px' }}>
                    <div
                      style={{ display: 'flex', flexWrap: 'nowrap', gap: 2, overflow: 'hidden' }}
                    >
                      {p.roles.map((r, ri) => (
                        <span
                          key={ri}
                          style={{
                            display: 'inline-block',
                            padding: '0px 4px',
                            borderRadius: 8,
                            background: GOB_COLORS[r.tipoRol] ?? C.go,
                            color: C.w,
                            fontSize: 7,
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {r.nombrePersonalizado ?? r.tipoRol.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td
                    style={{
                      padding: '2px 5px',
                      textAlign: 'center',
                      fontWeight: 800,
                      fontSize: 10,
                    }}
                  >
                    {p.numRoles || ''}
                  </td>
                </tr>
              ))}
              {filteredPersonas.length === 0 && (
                <tr>
                  <td
                    colSpan={10}
                    style={{
                      padding: 12,
                      textAlign: 'center',
                      color: C.go,
                      fontStyle: 'italic',
                      fontSize: 11,
                    }}
                  >
                    No se encontraron personas con los filtros aplicados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 0615k: viewer-only modal de persona (click en fila o link superior). */}
      <PersonaViewerModal personaId={viewerPersonaId} onClose={() => setViewerPersonaId(null)} />
    </div>
  );
}
